/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DockerContainer, DockerHost, getDockerContainerStats } from "@/api/docker";
import { DOCKER_REFRESH_INTERVALS } from "@/constants/dockerRefreshIntervals";
import { toResourcePoints, toNetworkTrafficPoints } from "@/utils/dockerStats";
import { formatBytes } from "@/utils/formatBytes";
import { MiniAreaChart } from "@/components/common/MiniAreaChart";
import { SNMPTrafficChart } from "@/components/snmp/SNMPTrafficChart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface DockerStatsTabProps {
  host: DockerHost;
  container: DockerContainer;
}

const HISTORY_WINDOWS = [60, 120, 240] as const;

export const DockerStatsTab: React.FC<DockerStatsTabProps> = ({ host, container }) => {
  const [limit, setLimit] = useState<number>(120);
  const { data: history, isLoading } = useQuery({
    queryKey: ["docker-container-stats", host.id, container.containerId, limit],
    queryFn: () => getDockerContainerStats(host.id, container.containerId, 1, limit),
    refetchInterval: DOCKER_REFRESH_INTERVALS.STATS,
  });

  const resourcePoints = history ? toResourcePoints(history.data) : [];
  const networkPoints = history ? toNetworkTrafficPoints(history.data) : [];

  if (isLoading) {
    return (
      <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
        Loading stats...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {resourcePoints.length} data points
        </p>
        <div
          className="flex items-center gap-1 rounded-md border border-gray-200 bg-gray-100/60 p-0.5 dark:border-gray-700 dark:bg-gray-800/50"
          title="History window (data points)"
        >
          {HISTORY_WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setLimit(w)}
              className={cn(
                "rounded px-2 py-0.5 text-[11px] tabular-nums transition-colors",
                limit === w
                  ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white"
                  : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200",
              )}
            >
              {w}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm sm:text-base">CPU Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <MiniAreaChart
            data={resourcePoints.map((p) => ({ time: p.time, value: p.cpuPercent }))}
            color="#d9b26a"
            gradientId="dockerCpuGradient"
            valueFormatter={(v) => `${v.toFixed(0)}%`}
            emptyLabel="No CPU history yet"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm sm:text-base">Memory Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <MiniAreaChart
            data={resourcePoints.map((p) => ({ time: p.time, value: p.memUsageBytes }))}
            color="#6e8f83"
            gradientId="dockerMemGradient"
            valueFormatter={(v) => formatBytes(v)}
            emptyLabel="No memory history yet"
          />
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader className="py-3">
          <CardTitle className="text-sm sm:text-base">Network I/O</CardTitle>
        </CardHeader>
        <CardContent>
          <SNMPTrafficChart data={networkPoints} />
        </CardContent>
      </Card>
      </div>
    </div>
  );
};
